import type {
  DraftRosterAssignment,
  DraftRosterSeat,
} from 'components/IncidentConfig/CreateIncident/createIncidentModel';
import type { FilledRosterAssignment } from 'hooks/rosterTemplateLibrary/filledRosterTemplateHooks';

/**
 * Flatten draft seat members into filled-template rows. Multiple people on one
 * seat become multiple assignments sharing that seatName.
 */
export function filledAssignmentsFromDraft(
  seats: DraftRosterSeat[],
  assignments: DraftRosterAssignment[],
  preservedContacts: FilledRosterAssignment[] = []
): FilledRosterAssignment[] {
  const seatNameById = new Map(
    seats.map((seat) => [seat.id, seat.name.trim()])
  );
  const seatNames = new Set(
    [...seatNameById.values()].filter((name) => Boolean(name))
  );

  const fromUsers: FilledRosterAssignment[] = [];
  for (const row of assignments) {
    const seatName = seatNameById.get(row.positionId);
    if (!seatName || !row.user_guid) continue;
    fromUsers.push({
      seatName,
      personKind: 'user',
      personId: row.user_guid,
      name: row.name,
      email: row.email_address || '',
      phone: '',
      userGuid: row.user_guid,
    });
  }

  const contacts = preservedContacts.filter(
    (row) =>
      (row.personKind === 'contact' || !row.userGuid) &&
      seatNames.has(row.seatName.trim())
  );

  return [...fromUsers, ...contacts];
}

/** Seed draft assignments from saved filled rows that have accounts. */
export function draftAssignmentsFromFilled(
  seats: DraftRosterSeat[],
  filled: FilledRosterAssignment[]
): DraftRosterAssignment[] {
  const idByName = new Map(seats.map((seat) => [seat.name.trim(), seat.id]));
  const next: DraftRosterAssignment[] = [];
  for (const row of filled) {
    if (!row.userGuid) continue;
    const positionId = idByName.get(row.seatName.trim());
    if (!positionId) continue;
    next.push({
      positionId,
      user_guid: row.userGuid,
      name: row.name,
      email_address: row.email || '',
      isLeader: false,
    });
  }
  return next;
}
